import { useState, useEffect, useMemo, useRef } from "react";
import {
  X,
  Plus,
  Loader2,
  ChevronDown,
  GraduationCap,
  Newspaper,
  Briefcase,
  FlaskConical,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useRefineContext,
  useRefine,
  useVerticals,
  useRoles,
  useRole,
  type ScenarioDetail,
  type BriefField,
} from "@/lib/api";
import {
  DynamicBriefFields,
  seedFieldValues,
  buildBriefData,
  isFieldEmpty,
  type FieldValue,
} from "@/components/DynamicBriefFields";

const LANGUAGES = [
  { code: "", label: "Same as source" },
  { code: "en", label: "English" },
  { code: "ru", label: "Russian" },
  { code: "es", label: "Spanish" },
  { code: "de", label: "German" },
  { code: "fr", label: "French" },
  { code: "pt", label: "Portuguese" },
  { code: "zh", label: "Chinese" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
  { code: "ar", label: "Arabic" },
];

const VERTICAL_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  education: GraduationCap,
  media: Newspaper,
  business: Briefcase,
  science: FlaskConical,
};
const iconForVertical = (slug: string | null | undefined) =>
  (slug && VERTICAL_ICONS[slug]) || FileText;

function flattenScenarios(
  categories: { intents: { scenarios: ScenarioDetail[] }[] }[],
): ScenarioDetail[] {
  return categories.flatMap((c) => c.intents.flatMap((i) => i.scenarios));
}

interface RefinePanelProps {
  jobId: string;
  currentRoleSlug: string | null;
  currentRoleName?: string | null;
  currentScenarioId: number | null;
  currentBriefData: Record<string, unknown>;
  /** Total reports this job has generated — gates regeneration at the 50 cap. */
  reportCount?: number;
  speakerCount: number;
  detectedLanguage: string;
  isOpen: boolean;
  onClose: () => void;
  /** Called once a regeneration is dispatched, with the output language it targets so the
   *  parent can point its report list at the right variant — or `undefined` when no explicit
   *  language was chosen, which mirrors what the request itself does: api.ts omits
   *  `output_language` when it is falsy, leaving the job on the language it already had.
   *  Reporting "" here instead would pin the view to the source-language variant while the
   *  server regenerates a different one. */
  onRefineStarted: (outputLanguage: string | undefined) => void;
  /** The report type of the currently active tab — enables "Current tab" scope. */
  currentReportType?: string;
}

export function RefinePanel({
  jobId,
  currentRoleSlug,
  currentRoleName,
  currentScenarioId,
  currentBriefData,
  reportCount = 0,
  speakerCount,
  detectedLanguage,
  isOpen,
  onClose,
  onRefineStarted,
  currentReportType,
}: RefinePanelProps) {
  const { data: existingContext } = useRefineContext(isOpen ? jobId : undefined);
  const refineMutation = useRefine();

  // Role / scenario / brief.
  const [roleSlug, setRoleSlug] = useState<string | null>(currentRoleSlug);
  const [scenarioId, setScenarioId] = useState<number | null>(currentScenarioId);
  const [fieldValues, setFieldValues] = useState<Record<string, FieldValue>>({});
  const [roleOpen, setRoleOpen] = useState(false);
  const [scenarioOpen, setScenarioOpen] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  const [dropdownVertical, setDropdownVertical] = useState<string | null>(
    currentRoleSlug ? currentRoleSlug.split("-")[0] ?? null : null,
  );

  // Kept context fields (preserved across role changes).
  const [speakerNames, setSpeakerNames] = useState<string[]>([]);
  const [recordingDescription, setRecordingDescription] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [keyTerms, setKeyTerms] = useState("");
  const [outputLanguage, setOutputLanguage] = useState("");
  const [scope, setScope] = useState<"all" | string>("all");

  // Pending role switch awaiting the new role's data; preserveOnce keeps brief
  // values when the auto-picked scenario matches the previous one by name. prevSlug
  // lets a failed load fall back to a known-good role instead of locking submit.
  const pendingRole = useRef<{ slug: string; prevName: string | null; prevSlug: string | null } | null>(
    null,
  );
  const preserveFieldsOnce = useRef(false);
  // True from the moment a new role is picked until its scenario has been re-derived —
  // blocks submit so scenarioId (still the old role's) can never go out with the new
  // role_slug.
  const [roleSwitchPending, setRoleSwitchPending] = useState(false);

  const { data: verticals } = useVerticals();
  const { data: dropdownRoles, isLoading: rolesLoading } = useRoles(dropdownVertical ?? undefined);
  const { data: role, isError: roleLoadFailed } = useRole(roleSlug ?? undefined);

  const scenarios = useMemo(() => (role ? flattenScenarios(role.categories) : []), [role]);
  const selectedScenario = useMemo(
    () => scenarios.find((s) => s.id === scenarioId) ?? null,
    [scenarios, scenarioId],
  );
  const briefFields = useMemo<BriefField[]>(
    () => selectedScenario?.brief_schema ?? [],
    [selectedScenario],
  );

  const roleVerticalSlug = role?.vertical_slug ?? (roleSlug ? roleSlug.split("-")[0] : null);
  const roleVerticalName =
    role?.vertical ?? verticals?.find((v) => v.slug === roleVerticalSlug)?.name ?? "";
  const roleDisplayName = role?.name ?? currentRoleName ?? roleSlug ?? "No role selected";

  const reportLimitReached = reportCount >= 50;
  const canScopeCurrent = !!currentReportType && currentReportType !== "SENTIMENT";

  useEffect(() => {
    if (!canScopeCurrent && scope === "current") setScope("all");
  }, [canScopeCurrent, scope]);

  // Re-sync role/scenario to the project's current values whenever the panel opens.
  useEffect(() => {
    if (!isOpen) return;
    setRoleSlug(currentRoleSlug);
    setScenarioId(currentScenarioId);
    setDropdownVertical(currentRoleSlug ? currentRoleSlug.split("-")[0] ?? null : null);
    setRoleOpen(false);
    setScenarioOpen(false);
    pendingRole.current = null;
    preserveFieldsOnce.current = false;
    setRoleSwitchPending(false);
    setRecordingDescription("");
    setTargetAudience("");
    setKeyTerms("");
    setSpeakerNames([]);
    setOutputLanguage("");
  }, [isOpen, currentRoleSlug, currentScenarioId]);

  // Once a freshly-selected role loads, auto-pick its scenario. Prefer a scenario
  // with the same name as the previous one (keep brief fields); else the first P0.
  useEffect(() => {
    const pending = pendingRole.current;
    if (!pending || !role || role.slug !== pending.slug) return;
    pendingRole.current = null;
    setRoleSwitchPending(false);
    const scs = flattenScenarios(role.categories);
    if (scs.length === 0) {
      setScenarioId(null);
      return;
    }
    const sameName = pending.prevName ? scs.find((s) => s.name === pending.prevName) : undefined;
    const pick = sameName ?? scs.find((s) => s.priority === "P0") ?? scs[0];
    if (sameName && pick && pick.id !== scenarioId) preserveFieldsOnce.current = true;
    if (pick) setScenarioId(pick.id);
  }, [role, scenarioId]);

  // A role switch whose fetch fails outright never reaches the effect above (it only
  // matches on a successful `role` load), so it would otherwise leave submit disabled
  // forever with no way out short of closing the panel. Fall back to the role that was
  // in effect before the switch instead.
  useEffect(() => {
    const pending = pendingRole.current;
    if (!pending || !roleLoadFailed || roleSlug !== pending.slug) return;
    pendingRole.current = null;
    setRoleSwitchPending(false);
    setRoleSlug(pending.prevSlug);
  }, [roleLoadFailed, roleSlug]);

  // Reconcile brief field values whenever the selected scenario changes. Seed from
  // the project's saved brief_data only for its original scenario; reset otherwise.
  // preserveFieldsOnce skips the reset for the same-named-scenario edge case.
  useEffect(() => {
    if (!selectedScenario) return;
    if (preserveFieldsOnce.current) {
      preserveFieldsOnce.current = false;
      return;
    }
    const seed = selectedScenario.id === currentScenarioId ? currentBriefData : null;
    setFieldValues(seedFieldValues(selectedScenario.brief_schema ?? [], seed));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScenario?.id]);

  // Pre-fill the kept context fields from existing refinement context.
  useEffect(() => {
    if (existingContext) {
      if (existingContext.speaker_names) setSpeakerNames(existingContext.speaker_names);
      if (existingContext.recording_description) setRecordingDescription(existingContext.recording_description);
      if (existingContext.target_audience) setTargetAudience(existingContext.target_audience);
      if (existingContext.key_terms) setKeyTerms(existingContext.key_terms);
      if (existingContext.output_language) setOutputLanguage(existingContext.output_language);
    }
  }, [existingContext]);

  useEffect(() => {
    if (speakerNames.length === 0 && speakerCount > 0) {
      setSpeakerNames(Array(speakerCount).fill(""));
    }
  }, [speakerCount, speakerNames.length]);

  const updateSpeakerName = (index: number, name: string) => {
    const updated = [...speakerNames];
    updated[index] = name;
    setSpeakerNames(updated);
  };
  const addSpeaker = () => setSpeakerNames([...speakerNames, ""]);

  const handleRoleChange = (newSlug: string) => {
    if (newSlug === roleSlug) {
      setRoleOpen(false);
      return;
    }
    // Carry the last *confirmed* role through a chained switch (pick B, then reconsider
    // to C before B ever resolves): roleSlug itself may still be an unconfirmed pick
    // whose scenario was never re-derived, so falling back to it would resurrect the
    // very mismatch this state exists to prevent.
    const lastConfirmedSlug = pendingRole.current ? pendingRole.current.prevSlug : roleSlug;
    pendingRole.current = { slug: newSlug, prevName: selectedScenario?.name ?? null, prevSlug: lastConfirmedSlug };
    setRoleSwitchPending(true);
    setRoleSlug(newSlug);
    setRoleOpen(false);
  };

  const handleScenarioChange = (newId: number) => {
    setScenarioId(newId);
    setScenarioOpen(false);
  };

  const filledCount = briefFields.filter((f) => !isFieldEmpty(f, fieldValues[f.key])).length;

  const handleSubmit = async () => {
    const resolvedScope =
      scope === "current" ? (canScopeCurrent ? (currentReportType as string) : "all") : scope;
    try {
      await refineMutation.mutateAsync({
        jobId: Number(jobId),
        roleSlug: roleSlug || undefined,
        scenarioId: scenarioId ?? undefined,
        briefData: briefFields.length > 0 ? buildBriefData(briefFields, fieldValues) : undefined,
        speakerNames: speakerNames.filter((n) => n.trim()),
        recordingDescription,
        targetAudience,
        keyTerms,
        outputLanguage,
        scope: resolvedScope,
      });
      onRefineStarted(outputLanguage || undefined);
      onClose();
    } catch (err) {
      console.error("Refine failed:", err);
    }
  };

  if (!isOpen) return null;

  const langLabel = detectedLanguage
    ? LANGUAGES.find((l) => l.code === detectedLanguage)?.label || detectedLanguage
    : "auto";
  const RoleIcon = iconForVertical(roleVerticalSlug);

  return (
    <div className="w-[300px] border-l bg-background flex flex-col h-full overflow-hidden flex-shrink-0">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold text-sm">Refine & regenerate</h3>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Role */}
        <div className="px-4 py-3 border-b">
          <label className="text-xs font-medium text-muted-foreground mb-2 block">Role</label>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-md bg-muted flex items-center justify-center shrink-0">
                <RoleIcon className="w-3.5 h-3.5 text-foreground" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{roleDisplayName}</p>
                {roleVerticalName ? (
                  <span className="inline-block text-[10px] uppercase tracking-wider text-muted-foreground">
                    {roleVerticalName}
                  </span>
                ) : null}
              </div>
            </div>
            <button
              onClick={() => setRoleOpen((o) => !o)}
              data-qa="refine-role-change-toggle"
              className="text-xs text-muted-foreground hover:text-foreground shrink-0"
            >
              Change
            </button>
          </div>

          {roleOpen ? (
            <div className="mt-3 rounded-lg border bg-muted/20 p-2 space-y-2">
              <div className="flex flex-wrap gap-1.5">
                {(verticals ?? []).map((v) => {
                  const Icon = iconForVertical(v.slug);
                  const active = dropdownVertical === v.slug;
                  return (
                    <button
                      key={v.slug}
                      onClick={() => setDropdownVertical(v.slug)}
                      className={
                        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs transition-colors " +
                        (active
                          ? "bg-foreground text-background"
                          : "bg-background border text-muted-foreground hover:bg-muted")
                      }
                    >
                      <Icon className="w-3 h-3" />
                      {v.name}
                    </button>
                  );
                })}
              </div>
              <div className="max-h-48 overflow-y-auto space-y-0.5">
                {rolesLoading ? (
                  <p className="text-xs text-muted-foreground px-1 py-1">Loading…</p>
                ) : (
                  (dropdownRoles ?? []).map((r) => (
                    <button
                      key={r.id}
                      onClick={() => handleRoleChange(r.slug)}
                      data-qa={`refine-role-option-${r.slug}`}
                      className={
                        "w-full text-left text-xs px-2 py-1.5 rounded-md transition-colors " +
                        (r.slug === roleSlug ? "bg-muted font-medium" : "hover:bg-muted/60")
                      }
                    >
                      {r.name}
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Scenario */}
        <div className="px-4 py-3 border-b">
          <label className="text-xs font-medium text-muted-foreground mb-2 block">Scenario</label>
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm truncate" data-qa="refine-scenario-name">
              {selectedScenario?.name ?? "—"}
            </p>
            <button
              onClick={() => setScenarioOpen((o) => !o)}
              disabled={scenarios.length === 0}
              className="text-xs text-muted-foreground hover:text-foreground shrink-0 disabled:opacity-40"
            >
              Change
            </button>
          </div>
          {scenarioOpen && scenarios.length > 0 ? (
            <div className="mt-2 rounded-lg border bg-muted/20 p-1 max-h-48 overflow-y-auto space-y-0.5">
              {scenarios.map((s) => (
                <button
                  key={s.id}
                  onClick={() => handleScenarioChange(s.id)}
                  className={
                    "w-full text-left text-xs px-2 py-1.5 rounded-md transition-colors " +
                    (s.id === scenarioId ? "bg-muted font-medium" : "hover:bg-muted/60")
                  }
                >
                  {s.name}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {/* Brief settings (collapsible) */}
        <div className="px-4 py-3 border-b">
          <button
            onClick={() => setBriefOpen((o) => !o)}
            className="w-full flex items-center justify-between"
          >
            <span className="text-xs font-medium text-muted-foreground">Brief settings</span>
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground/70">
              {briefFields.length > 0 ? `${filledCount} of ${briefFields.length} filled` : "No fields"}
              <ChevronDown className={"w-3.5 h-3.5 transition-transform " + (briefOpen ? "rotate-180" : "")} />
            </span>
          </button>
          {briefOpen ? (
            briefFields.length > 0 ? (
              <div className="mt-3 space-y-4">
                <DynamicBriefFields
                  fields={briefFields}
                  values={fieldValues}
                  onChange={(key, value) => setFieldValues((prev) => ({ ...prev, [key]: value }))}
                />
              </div>
            ) : (
              <p className="text-xs text-muted-foreground mt-2">
                This scenario has no role-specific fields.
              </p>
            )
          ) : null}
        </div>

        {/* Speakers */}
        <div className="px-4 py-3 border-b">
          <label className="text-xs font-medium text-muted-foreground mb-2 block">
            Speakers{" "}
            <span className="font-normal text-muted-foreground/60">(detected: {speakerCount})</span>
          </label>
          <div className="space-y-1.5">
            {speakerNames.map((name, i) => (
              <input
                key={i}
                type="text"
                value={name}
                onChange={(e) => updateSpeakerName(i, e.target.value)}
                placeholder={`Speaker ${i + 1} name`}
                className="w-full text-xs px-3 py-1.5 rounded-md border bg-background focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            ))}
            <button
              onClick={addSpeaker}
              className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 pt-1"
            >
              <Plus className="w-3 h-3" /> Add speaker
            </button>
          </div>
        </div>

        {/* Context */}
        <div className="px-4 py-3 border-b">
          <label className="text-xs font-medium text-muted-foreground mb-2 block">Context</label>
          <div className="space-y-1.5">
            <input
              type="text"
              value={recordingDescription}
              onChange={(e) => setRecordingDescription(e.target.value)}
              placeholder="What is this recording?"
              className="w-full text-xs px-3 py-1.5 rounded-md border bg-background focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <input
              type="text"
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              placeholder="Target audience"
              className="w-full text-xs px-3 py-1.5 rounded-md border bg-background focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <textarea
              value={keyTerms}
              onChange={(e) => setKeyTerms(e.target.value)}
              placeholder="Key names & terms"
              rows={3}
              className="w-full text-xs px-3 py-1.5 rounded-md border bg-background focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y"
            />
          </div>
        </div>

        {/* Output language */}
        <div className="px-4 py-3 border-b">
          <label className="text-xs font-medium text-muted-foreground mb-2 block">Output language</label>
          <select
            data-qa="refine-output-language"
            value={outputLanguage}
            onChange={(e) => setOutputLanguage(e.target.value)}
            className="w-full text-xs px-3 py-1.5 rounded-md border bg-background focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {LANGUAGES.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.code === "" ? `Same as source (${langLabel})` : lang.label}
              </option>
            ))}
          </select>
        </div>

        {/* Scope */}
        <div className="px-4 py-3 border-b">
          <label className="text-xs font-medium text-muted-foreground mb-2 block">Regenerate scope</label>
          <div className="flex gap-1.5">
            <button
              onClick={() => setScope("all")}
              className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                scope === "all"
                  ? "bg-blue-100 text-blue-700 font-medium"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              All reports
            </button>
            {canScopeCurrent && (
              <button
                onClick={() => setScope("current")}
                className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                  scope === "current"
                    ? "bg-blue-100 text-blue-700 font-medium"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                Current tab
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Submit button */}
      <div className="px-4 py-3 border-t">
        <Button
          onClick={handleSubmit}
          disabled={refineMutation.isPending || reportLimitReached || roleSwitchPending}
          data-qa="refine-submit"
          className="w-full"
        >
          {refineMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Regenerating...
            </>
          ) : (
            "Regenerate reports"
          )}
        </Button>
        <p className={"text-xs mt-2 " + (reportLimitReached ? "text-red-500" : "text-muted-foreground")}>
          {reportLimitReached
            ? "Report limit reached for this project"
            : `${reportCount} report${reportCount === 1 ? "" : "s"} generated`}
        </p>
        {refineMutation.isError && (
          <p className="text-xs text-red-500 mt-2">
            {refineMutation.error?.message || "Failed to regenerate"}
          </p>
        )}
      </div>
    </div>
  );
}
